import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { isMobile } from "react-device-detect";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ControlledFieldWithKey } from "../../../../common/models/ControlledFieldWithKey";
import { StepMenuLandingProps } from "../../../../components/step-menu/StepMenu";
import StepMenuLandingCardList from "../../../../components/step-menu/StepMenuLandingCardList";
import StepMenuLandingContainer from "../../../../components/step-menu/StepMenuLandingContainer";
import { useConfirmation } from "../../../../contexts/confirmation-context/ConfirmationContext";
import ListDragOverlay from "../../../list-drag-overlay/ListDragOverlay";
import { VirtualizedListItem } from "../../../virtualized-list/VirtualizedList";
import { allTagGroupsValid, createDefaultTagGroupField } from "../ObjectTagValuesConfigurationEditorUtils";
import { ObjectTagValuesConfigurationFieldType, ObjectTagValuesTagGroupConfigurationFieldType, TagGroupFieldKeyType } from "../ObjectTagValuesConfigurationFieldTypes";
import LandingTagGroupCard from "./LandingTagGroupCard";

type TagGroupsFieldArrayType = Array<TagGroupsFieldArrayItemType>;
type TagGroupsFieldArrayItemType = ControlledFieldWithKey<ObjectTagValuesTagGroupConfigurationFieldType, "fieldID", TagGroupFieldKeyType, "tagGroupKey"> & VirtualizedListItem;

export type LandingProps = {
    onEditTagGroup?: (tagGroupKey: TagGroupFieldKeyType) => void;
} & StepMenuLandingProps;

export default function Landing({
    onEditTagGroup
}: LandingProps) {

    const { t } = useTranslation();
    const { confirm } = useConfirmation();

    const {
        watch
    } = useFormContext<ObjectTagValuesConfigurationFieldType>();

    const {
        fields,
        append,
        remove,
        move
    } = useFieldArray<ObjectTagValuesConfigurationFieldType, "tagGroups", "fieldID">({ name: "tagGroups", keyName: "fieldID" });

    const watchTagGroups = watch("tagGroups");

    const tagGroups: TagGroupsFieldArrayType = fields.map((field, index) => {
        return {
            ...field,
            ...watchTagGroups[index],
            tagGroupKey: `tagGroups.${index}`
        };
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: isMobile ? 500 : 0,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // #region Event Handlers

    function onAddTagGroup() {
        const TagGroup: ObjectTagValuesTagGroupConfigurationFieldType = createDefaultTagGroupField(`New Tag Group ${tagGroups.length}`);
        append(TagGroup, { shouldFocus: false });
    }

    function onDuplicateTagGroup(tagGroupKey: TagGroupFieldKeyType) {
        duplicateTagGroup(tagGroupKey);
    }

    function onDeleteTagGroup(tagGroupKey: TagGroupFieldKeyType) {
        deleteTagGroup(tagGroupKey);
    }

    // #endregion Event Handlers

    // #region Tag Group Methods

    function duplicateTagGroup(tagGroupKey: TagGroupFieldKeyType) {
        const tagGroup = tagGroups.find(s => s.tagGroupKey === tagGroupKey);

        if (tagGroup) {
            // Strip fieldID from tagGroup so the field array can assign a unique value.
            const { fieldID, ...rawTagGroup } = tagGroup;
            const duplicate = {
                ...rawTagGroup,
                //equatableKey: UUID.generate(),
                name: `${tagGroup?.name ?? ""} (copy)`,
                //columns: TagGroup?.columns.map(column => ({ ...column })),
                //expanded: true
            };

            append(duplicate, { shouldFocus: false });
        }
    }

    async function deleteTagGroup(tagGroupKey: TagGroupFieldKeyType) {
        const canDeleteTagGroup = await getDeleteTagGroupConfirmation();

        if (canDeleteTagGroup) {
            const index = tagGroups.findIndex(s => s.tagGroupKey === tagGroupKey);

            remove(index);
        }
    }

    // #endregion TagGroup Methods

    const disableAddTagGroupButton = !allTagGroupsValid(tagGroups);

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = tagGroups.findIndex(tagGroup => tagGroup.name === active.id);
            const newIndex = tagGroups.findIndex(tagGroup => tagGroup.name === over?.id);

            if (oldIndex !== null && newIndex !== null) {
                move(oldIndex, newIndex);
            }
        }
    }

    // #region Dialog functions

    async function getDeleteTagGroupConfirmation() {

        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("configuration.types.tagGroup") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    // #endregion Dialog functions

    return (
        <StepMenuLandingContainer>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
                <SortableContext
                    items={tagGroups.map(tagGroup => tagGroup.name)}
                    strategy={verticalListSortingStrategy}
                >
                    <StepMenuLandingCardList
                        hideExpandAllButton={true}
                        hideCollapseAllButton={true}
                        onAddSection={onAddTagGroup}
                        info={t("objectTagValuesConfiguration.steps.landing.info")}
                        disableAddSectionButton={disableAddTagGroupButton}
                        addSectionButtonLabel={t("objectTagValuesConfiguration.steps.landing.addTagGroup")}
                        scrollable={true}
                    >
                        {tagGroups.map(tagGroup => {
                            return (
                                <LandingTagGroupCard
                                    key={tagGroup.fieldID}
                                    tagGroupKey={tagGroup.tagGroupKey}
                                    duplicateEnabled={!disableAddTagGroupButton}
                                    deleteEnabled={tagGroups.length > 1}
                                    onEdit={() => onEditTagGroup && onEditTagGroup(tagGroup.tagGroupKey)}
                                    onDuplicate={onDuplicateTagGroup}
                                    onDelete={onDeleteTagGroup}
                                />
                            );
                        })}
                    </StepMenuLandingCardList>
                    <ListDragOverlay />
                </SortableContext>
            </DndContext>
        </StepMenuLandingContainer>
    );
}
